'use client';

import Link from 'next/link';
import PropTypes from 'prop-types';
import { Box, Container, Typography } from '@mui/material';
import MarketingVideoEmbed from './MarketingVideoEmbed';
import NewsArticleImageCarousel from './NewsArticleImageCarousel';
import NewsMediaPageHeader from './NewsMediaPageHeader';
import { PRODUCT_MANUALS_PATH } from '@/consts/routes';

function normalizeInstructionImages(manual) {
  const images = manual.instructionImages ?? manual.instruction_images ?? [];
  return images.map((image, index) => ({
    src: image.src ?? image.file_url ?? image.fileUrl,
    alt: image.alt ?? image.alt_text ?? image.altText ?? `${manual.title} — step ${index + 1}`,
  }));
}

export default function ProductManualDetailPage({ manual }) {
  const videoUrl =
    manual.videoEmbedUrl ?? manual.video_embed_url ?? manual.videoUrl ?? manual.video_url;
  const instructionImages = normalizeInstructionImages(manual);

  return (
    <>
      <Box component="section" sx={{ pt: { xs: 2, md: 3 } }}>
        <Container maxWidth="lg">
          <Typography
            component="nav"
            aria-label="Breadcrumb"
            sx={{ mb: 2, fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase' }}
          >
            <Box
              component={Link}
              href={PRODUCT_MANUALS_PATH}
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: 'text.primary' },
              }}
            >
              Product Manuals
            </Box>
          </Typography>
        </Container>
      </Box>
      {manual.intro ? (
        <NewsMediaPageHeader title={manual.title} introCopy={manual.intro} />
      ) : (
        <Box component="section" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 3, md: 4 } }}>
          <Container maxWidth="lg">
            <Typography component="h1" variant="h4" sx={{ fontWeight: 500, m: 0 }}>
              {manual.title}
            </Typography>
          </Container>
        </Box>
      )}

      <Box component="section" sx={{ pb: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          {videoUrl ? (
            <MarketingVideoEmbed src={videoUrl} title={`${manual.title} installation video`} />
          ) : null}
          <NewsArticleImageCarousel
            images={instructionImages}
            title={manual.title}
            imageOpacity={0.6}
          />
        </Container>
      </Box>
    </>
  );
}

ProductManualDetailPage.propTypes = {
  manual: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    intro: PropTypes.string,
    videoEmbedUrl: PropTypes.string,
    video_embed_url: PropTypes.string,
    videoUrl: PropTypes.string,
    video_url: PropTypes.string,
    instructionImages: PropTypes.arrayOf(
      PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
      }),
    ),
    instruction_images: PropTypes.arrayOf(
      PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
      }),
    ),
  }).isRequired,
};
